// src/components/managed-sources/CategoriesStep.tsx

import {
  Badge,
  Checkbox,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBuildingWarehouse,
  IconChartBar,
  IconCurrencyDollar,
  IconDevices,
  IconPackage,
  IconShoppingCart,
  IconUsers,
} from "@tabler/icons-react";
import type { ManagedSourceCategory } from "@/types/managedSources";

type Props = {
  categories: ManagedSourceCategory[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Categories that are unavailable due to missing source tables (transform-only) */
  unavailableCategories?: string[];
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  ad_performance: <IconChartBar size={20} />,
  conversions: <IconCurrencyDollar size={20} />,
  platform_placement: <IconDevices size={20} />,
  contacts: <IconUsers size={20} />,
  // Shopify
  orders: <IconShoppingCart size={20} />,
  customers: <IconUsers size={20} />,
  products: <IconPackage size={20} />,
  inventory: <IconBuildingWarehouse size={20} />,
};

export default function CategoriesStep({
  categories,
  selected,
  onChange,
  unavailableCategories,
}: Props) {
  const unavailableSet = new Set(unavailableCategories ?? []);

  function toggle(key: string) {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  }

  return (
    <Stack gap="lg">
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="blue">
          <IconChartBar size={24} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Choose your data
          </Text>
          <Text size="sm" c="dimmed">
            Select which data categories to include in your pipeline. You can
            change this later.
          </Text>
        </Stack>
      </Group>

      <Stack gap="xs">
        {categories.map((cat) => {
          const isMissingTables = unavailableSet.has(cat.key);
          const isDisabled = !cat.available || isMissingTables;
          const isSelected = selected.includes(cat.key) && !isDisabled;
          return (
            <Paper
              key={cat.key}
              withBorder
              p="md"
              radius="md"
              style={{
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
                borderColor: isSelected
                  ? "var(--mantine-color-blue-5)"
                  : undefined,
                backgroundColor: isSelected
                  ? "var(--mantine-color-blue-0)"
                  : undefined,
              }}
              onClick={() => !isDisabled && toggle(cat.key)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => !isDisabled && toggle(cat.key)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isDisabled}
                    styles={{
                      input: {
                        cursor: isDisabled ? "not-allowed" : "pointer",
                      },
                    }}
                  />
                  {CATEGORY_ICONS[cat.key] ?? <IconChartBar size={20} />}
                  <Stack gap={2}>
                    <Text fw={500} size="sm">
                      {cat.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {cat.description}
                    </Text>
                    {cat.example_metrics?.length > 0 && (
                      <Text size="xs" c="dimmed" fs="italic">
                        e.g. {cat.example_metrics.slice(0, 3).join(", ")}
                      </Text>
                    )}
                  </Stack>
                </Group>
                {!cat.available && !isMissingTables && (
                  <Badge variant="light" color="gray" size="xs">
                    Coming soon
                  </Badge>
                )}
                {isMissingTables && (
                  <Badge variant="light" color="orange" size="xs">
                    Missing source tables
                  </Badge>
                )}
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}
